/**
 * Global onboarding wizard for first-time users.
 *
 * Guides users through the three prerequisites for starting their first
 * coding session:
 *
 *   Step 1 – Models: Review available AI models, optionally connect commercial ones
 *   Step 2 – GitHub: Install the GitHub App (skippable for current session)
 *   Step 3 – Repository: Choose a codebase (skippable with session-scoped dismiss)
 *
 * After step 3, the user is redirected into the per-repo onboarding
 * wizard (build config → environment → first build) which already exists.
 *
 * Step 1 is ALWAYS shown to new users (never auto-skipped) so they can
 * see what models they have and optionally upgrade. The "Continue" button
 * in Zone 1 advances them past free models without requiring any connection.
 */

import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Check,
  ArrowRight,
  Github,
  Loader2,
  Search,
  Eye,
  EyeOff,
  X,
  ExternalLink,
  Copy,
  ChevronDown,
  Key,
  Terminal,
  CreditCard,
} from "lucide-react";
import { api } from "@/lib/api";
import { authClient } from "@/lib/auth";
import { cn, langColors } from "@/lib/utils";
import { useMobileLayout } from "@/hooks/use-mobile-layout";
import { useMarkOnboardingProgress, useOnboardingProgress } from "@/hooks/use-onboarding-progress";
import {
  normalizeArrayResponse,
  normalizeGitHubReposRouteResponse,
} from "@/lib/repository-route-response-normalizers";
import { EmptyRepositoryBadge } from "@/components/ui/empty-repository-badge";
import { API_KEY_PROVIDERS } from "@/config/providers";
import { invalidateModelProviderQueries } from "@/lib/invalidate-model-provider-queries";
import {
  hasSeenModelsStep,
  markModelsStepSeen,
  markGithubStepSkipped,
  markOnboardingComplete,
  markRepoStepSkipped,
} from "@/lib/onboarding-progress";
import { PostRepoDecisionCard } from "@/components/onboarding/PostRepoDecisionCard";
import type { ImportedRepositoryDto } from "@/components/repositories/ImportPublicRepoCard";
import type { CreatedProjectDto } from "@/components/repositories/NewProjectCard";
import { SmartProjectInput } from "@/components/onboarding/SmartProjectInput";
import { DemoLaunchModal } from "@/components/demos/DemoLaunchModal";
import { DEMO_PROJECTS } from "@cord/shared";
import { upsertRepositoryInCache } from "@/lib/repository-list-cache";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export interface GettingStartedSearch {
  /** Set by the GitHub App setup callback when returning from GitHub */
  github_app?: "connected" | "error";
  /** Error reason from the GitHub App setup callback */
  reason?: string;
}

export const Route = createFileRoute("/_authed/getting-started")({
  component: GettingStartedPage,
  validateSearch: (search: Record<string, unknown>): GettingStartedSearch => {
    const githubApp = search.github_app === "connected" || search.github_app === "error"
      ? search.github_app
      : undefined;
    const reason = typeof search.reason === "string" ? search.reason : undefined;
    return { github_app: githubApp, reason };
  },
});

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type OnboardingStep = 1 | 2 | 3;

const STEPS: Array<{ step: OnboardingStep; label: string }> = [
  { step: 1, label: "Models" },
  { step: 2, label: "Repository source" },
  { step: 3, label: "Repository" },
];

/**
 * Interval for polling ChatGPT device auth completion.
 * The server returns a recommended interval but we floor it at 1s.
 */
const CHATGPT_POLL_MIN_INTERVAL_S = 1;

/**
 * Maximum duration in milliseconds to poll for ChatGPT device auth
 * before giving up and showing a timeout message. Device auth tokens
 * typically expire in 15–30 minutes on the server side; we time out
 * earlier on the client to avoid a permanently spinning state.
 */
const CHATGPT_POLL_MAX_DURATION_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Maximum duration (ms) to poll for Claude CLI OAuth connection before
 * stopping. Prevents indefinite background polling if the user expands
 * the Claude row and forgets about it. 10 minutes mirrors the ChatGPT
 * device auth timeout for consistency.
 */
const CLAUDE_POLL_MAX_DURATION_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Maximum time (ms) to wait after triggering GitHub navigation before
 * assuming the redirect was blocked (e.g. popup blocker, browser extension).
 * 5 seconds is generous — normal GitHub redirects resolve within 1-2s, but
 * slow connections may take longer. After this timeout the button resets so
 * the user can retry.
 */
const CONNECT_GITHUB_NAVIGATION_TIMEOUT_MS = 5000;

/**
 * Maximum number of repos to show in the dropdown before truncation.
 * This keeps the DOM lightweight for orgs with hundreds of repos.
 */
const REPO_DROPDOWN_MAX_ITEMS = 50;

/**
 * Brief delay (ms) before auto-advancing after a successful connection.
 * Gives the user a moment to see the success state before the UI changes.
 */
const SUCCESS_ADVANCE_DELAY_MS = 800;

const ACTIVE_GITHUB_APP_INSTALLATION_STATUS = "active";
const GITHUB_APP_INSTALLATIONS_QUERY_KEY = ["github-app-installations"] as const;

/**
 * Open-source models included free with every workspace. Displayed in
 * Step 1 Zone 1 so users know what's available without any setup.
 */
const FREE_MODELS = [
  "Kimi K2.6",
  "Qwen 3.6",
  "DeepSeek V3.2",
  "MiniMax M2.7",
  "Gemma 4",
  "GLM 5.1",
];

/**
 * Secondary info toast shown whenever the user advances past the model
 * step — regardless of which path they took (free-models Continue, API
 * key, Claude, ChatGPT). The goal is to make it explicit that the free
 * open-source models are always granted, so users who bring their own
 * key still know the free models are available.
 *
 * A separate toast (rather than combining with the primary success toast)
 * keeps each path's primary message intact and lets the info message be
 * consistent across every surface that dispatches it.
 */
const FREE_MODELS_INFO_TOAST_MESSAGE = `${FREE_MODELS.length} free open-source models are included on every account — no setup needed.`;

/**
 * Small delay before showing the secondary free-models info toast so it
 * doesn't collide visually with the primary success toast that triggered
 * it. sonner stacks toasts naturally, but a tiny delay makes the two
 * messages feel sequential rather than simultaneous. 400 ms is long enough
 * for the primary toast to appear first and short enough that users don't
 * perceive a noticeable gap.
 */
const FREE_MODELS_INFO_TOAST_DELAY_MS = 400;

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

interface GitHubRepo {
  id: number;
  full_name: string;
  name: string;
  /** Empty string means the GitHub repo has no commits yet. */
  default_branch: string;
  clone_url: string;
  private: boolean;
  language: string | null;
  github_installation_id: string;
  source_scope: "org" | "external";
}

interface GitHubReposResponse {
  repos: GitHubRepo[];
  installationErrors: Array<{ scope: "org" | "external"; message: string }>;
}

interface ConnectedRepo {
  id: string;
  provider?: "github" | "gitea" | string;
  githubId: number | null;
  githubFullName: string;
  displayName?: string | null;
  repositoryFullName?: string | null;
  name: string;
  defaultBranch?: string | null;
  language: string | null;
}

interface GitHubAppInstallationSummary {
  id: string;
  displayName?: string;
  ownerUserId?: string | null;
  status: string;
}

interface ChatGptOauthStartResponse {
  deviceAuthId: string;
  userCode: string;
  verificationUrl: string;
  intervalSeconds: number;
}

interface ClaudeOauthStatus {
  connected: boolean;
}

interface ChatGptOauthCompleteResponse {
  status: "connected" | "pending";
}

function isActiveGitHubAppInstallation(args: { installation: GitHubAppInstallationSummary }): boolean {
  return args.installation.status === ACTIVE_GITHUB_APP_INSTALLATION_STATUS;
}

function hasActiveGitHubAppInstallations(args: {
  installations: GitHubAppInstallationSummary[] | null | undefined;
}): boolean {
  return (args.installations ?? []).some((installation) => isActiveGitHubAppInstallation({ installation }));
}

async function fetchGitHubAppInstallations(args: { apiBase: string }): Promise<GitHubAppInstallationSummary[]> {
  const url = args.apiBase
    ? `${args.apiBase}/api/github/app/installations`
    : "/api/github/app/installations";
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) return [];
  return res.json() as Promise<GitHubAppInstallationSummary[]>;
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function GettingStartedPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobileLayout = useMobileLayout();
  const { data: authSession } = authClient.useSession();
  // `_authed` layout guarantees session?.user?.id is populated before this
  // route renders. Fall back to empty string to keep the helpers safe; the
  // helpers no-op on empty userId.
  const userId = authSession?.user?.id ?? "";
  const activeOrganizationId = authSession?.session?.activeOrganizationId ?? null;
  const { data: onboardingProgress, isLoading: onboardingProgressLoading } = useOnboardingProgress({
    userId,
    organizationId: activeOrganizationId,
  });
  const markProgress = useMarkOnboardingProgress();
  const { github_app: githubAppResult, reason: githubErrorReason } = useSearch({
    from: "/_authed/getting-started",
  });
  const apiBase = import.meta.env.VITE_API_URL || "";
  const [postRepoDecisionActive, setPostRepoDecisionActive] = useState(false);

  // Ref-based dedup for the shared "free models are included" info toast.
  // Component-scoped (not module-scoped) so that logging in as a different
  // user in the same tab, or navigating back to /getting-started after
  // finishing onboarding, re-enables the toast. Cleared on unmount so a
  // pending setTimeout (see FREE_MODELS_INFO_TOAST_DELAY_MS) doesn't fire
  // after the user has already navigated away from the wizard.
  const freeModelsInfoToastShownRef = useRef(false);
  const freeModelsInfoToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (freeModelsInfoToastTimeoutRef.current !== null) {
        clearTimeout(freeModelsInfoToastTimeoutRef.current);
        freeModelsInfoToastTimeoutRef.current = null;
      }
    };
  }, []);

  const notifyFreeModelsIncluded = useCallback(() => {
    if (freeModelsInfoToastShownRef.current) return;
    freeModelsInfoToastShownRef.current = true;
    // Cancel any prior pending toast (defence-in-depth; shouldn't happen
    // given the shown-ref guard, but keeps the refs consistent).
    if (freeModelsInfoToastTimeoutRef.current !== null) {
      clearTimeout(freeModelsInfoToastTimeoutRef.current);
    }
    freeModelsInfoToastTimeoutRef.current = setTimeout(() => {
      freeModelsInfoToastTimeoutRef.current = null;
      toast.info(FREE_MODELS_INFO_TOAST_MESSAGE);
    }, FREE_MODELS_INFO_TOAST_DELAY_MS);
  }, []);

  // ---- Handle GitHub callback redirect params ----
  const githubCallbackHandledRef = useRef(false);
  useEffect(() => {
    if (!githubAppResult || githubCallbackHandledRef.current) return;
    githubCallbackHandledRef.current = true;

    void (async () => {
      if (githubAppResult === "error") {
        let refreshedInstallations: GitHubAppInstallationSummary[] = [];
        try {
          refreshedInstallations = await fetchGitHubAppInstallations({ apiBase });
        } catch {
          refreshedInstallations = [];
        }
        queryClient.setQueryData(GITHUB_APP_INSTALLATIONS_QUERY_KEY, refreshedInstallations);
        if (hasActiveGitHubAppInstallations({ installations: refreshedInstallations })) {
          toast.success("GitHub App is connected. Continuing onboarding…");
          navigate({ to: "/getting-started", search: {}, replace: true });
          return;
        }

        const GITHUB_ERROR_MESSAGES: Record<string, string> = {
          external_repos_disabled: "External repos are disabled by your organization.",
          session_expired: "GitHub connection timed out. Please try again.",
          invalid_state: "GitHub connection was interrupted. Please try again.",
          install_request_pending: "GitHub sent the install request to an organization owner. Once it is approved, come back to Cord to continue.",
          missing_installation_id: "GitHub didn't complete the installation. Please try again.",
          missing_context: "GitHub connection session was lost. Please try again.",
          setup_failed: "GitHub setup encountered an error. Please try again.",
        };
        const friendlyReason = (githubErrorReason && GITHUB_ERROR_MESSAGES[githubErrorReason])
          ?? githubErrorReason
          ?? "GitHub setup failed. Please try again.";
        toast.error(friendlyReason);
      }

      // Invalidate queries so the wizard sees the new installation
      queryClient.invalidateQueries({ queryKey: GITHUB_APP_INSTALLATIONS_QUERY_KEY });

      // Strip callback params from URL
      navigate({ to: "/getting-started", search: {}, replace: true });
    })();
  }, [apiBase, githubAppResult, githubErrorReason, navigate, queryClient]);

  // ---- Data queries to determine step completion ----

  const { data: hasProviderData, isLoading: hasProviderLoading } = useQuery({
    queryKey: ["has-provider"],
    queryFn: async () => {
      const res = await api.api.settings["has-provider"].$get();
      if (!res.ok) return { hasProvider: false };
      return res.json() as Promise<{ hasProvider: boolean }>;
    },
    staleTime: 5_000,
  });

  const { data: githubInstallations, isLoading: githubLoading } = useQuery<GitHubAppInstallationSummary[]>({
    queryKey: GITHUB_APP_INSTALLATIONS_QUERY_KEY,
    queryFn: () => fetchGitHubAppInstallations({ apiBase }),
    staleTime: 5_000,
  });

  const { data: repos, isLoading: reposLoading } = useQuery<ConnectedRepo[]>({
    queryKey: ["repos"],
    queryFn: async () => {
      const res = await api.api.repos.$get();
      if (!res.ok) return [];
      return normalizeArrayResponse<ConnectedRepo>({ value: await res.json() });
    },
  });

  const { data: orgPolicy } = useQuery<{
    allowExternalRepos: boolean;
    adminOnlyOrgRepoConnect: boolean;
    canManage: boolean;
  }>({
    queryKey: ["org-policy"],
    queryFn: async () => {
      const url = apiBase ? `${apiBase}/api/settings/org-policy` : "/api/settings/org-policy";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        return {
          allowExternalRepos: true,
          adminOnlyOrgRepoConnect: false,
          canManage: false,
        };
      }
      return res.json();
    },
  });
  const allowPublicImportExternalScope = orgPolicy?.allowExternalRepos ?? true;
  const allowPublicImportOrgScope = !(orgPolicy?.adminOnlyOrgRepoConnect) || Boolean(orgPolicy?.canManage);

  // ---- Derived completion state ----

  const hasProvider = hasProviderData?.hasProvider ?? false;
  const hasGitHub = hasActiveGitHubAppInstallations({ installations: githubInstallations });
  const hasRepo = (repos ?? []).length > 0;

  const onboardingProgressReady = Boolean(activeOrganizationId) && !onboardingProgressLoading;
  const isDataReady = !hasProviderLoading && !githubLoading && !reposLoading && onboardingProgressReady;

  // ---- Step 1 acknowledgment ----
  // Step 1 never auto-advances based on provider state. The user must
  // explicitly click "Continue" or complete a commercial connection.
  const [localStep1Acknowledged, setLocalStep1Acknowledged] = useState(() => {
    // If user previously saw models step, skip step 1 on revisit.
    return hasSeenModelsStep(userId);
  });
  const step1Acknowledged = localStep1Acknowledged || onboardingProgress?.modelsStepSeen === true;
  useEffect(() => {
    setLocalStep1Acknowledged(hasSeenModelsStep(userId));
  }, [userId]);
  useEffect(() => {
    if (onboardingProgress?.modelsStepSeen) {
      setLocalStep1Acknowledged(true);
      markModelsStepSeen(userId);
    }
  }, [onboardingProgress?.modelsStepSeen, userId]);

  // ---- Current step (auto-advance past completed steps) ----

  const computeCurrentStep = useCallback((): OnboardingStep => {
    // Step 1: must be explicitly acknowledged (not based on hasProvider)
    if (!step1Acknowledged) return 1;
    // Step 2: GitHub is optional now; users can import a public repo instead.
    if (!hasGitHub && !hasRepo) return 2;
    // Step 3: must have a repo
    return 3;
  }, [step1Acknowledged, hasGitHub, hasRepo]);

  const [currentStep, setCurrentStep] = useState<OnboardingStep>(() => {
    if (!step1Acknowledged) return 1;
    return 1; // Will be computed properly once data loads
  });

  // Auto-advance on data changes
  useEffect(() => {
    if (!isDataReady) return;

    // If the user connected a repo in this very flow, keep them on the
    // post-repo decision card. The `repos` query invalidation will otherwise
    // make `hasRepo` true and this parent effect would navigate away before
    // they can choose "Start coding" vs "Set up dev environment".
    if (postRepoDecisionActive) return;

    // If they already have everything, mark complete and redirect to dashboard
    if (step1Acknowledged && hasRepo) {
      markOnboardingComplete(userId);
      if (onboardingProgress?.completed !== true) {
        markProgress.mutate({ completed: true });
      }
      navigate({ to: "/" });
      return;
    }

    setCurrentStep(computeCurrentStep());
  }, [isDataReady, step1Acknowledged, hasProvider, hasGitHub, hasRepo, postRepoDecisionActive, computeCurrentStep, navigate, userId, onboardingProgress?.completed, markProgress.mutate]);

  const handleStep1Complete = useCallback(() => {
    markModelsStepSeen(userId);
    setLocalStep1Acknowledged(true);
    markProgress.mutate({ modelsStepSeen: true });
    // Query invalidation for provider state (in case they connected something)
    queryClient.invalidateQueries({ queryKey: ["has-provider"] });
    // Confirm the free open-source models are granted, regardless of which
    // path the user took through the model step (free Continue, API key,
    // Claude, or ChatGPT). Keeps the message consistent and makes it
    // explicit that commercial providers are additive, not replacements.
    notifyFreeModelsIncluded();
  }, [queryClient, userId, notifyFreeModelsIncluded, markProgress.mutate]);

  const handleSkipGitHub = useCallback(async () => {
    markGithubStepSkipped(userId);
    try {
      await markProgress.mutateAsync({ githubStepSkipped: true });
    } catch {
      toast.warning("Skipped in this browser, but we could not save that across devices.");
    }
    navigate({ to: "/" });
  }, [navigate, userId, markProgress.mutateAsync]);

  // ---- Loading state ----

  if (!isDataReady || onboardingProgressLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-zinc-950">
        <div className="flex flex-col items-center gap-3 animate-[fade-in_0.2s_ease-out]">
          <div className="size-10 overflow-hidden rounded-xl">
            <img src="/cord-logo.svg" alt="Cord" className="size-full" />
          </div>
          <div className="text-sm text-zinc-500">Loading…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-zinc-950">
      {/* Radial glow */}
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_top,rgba(39,39,42,0.4)_0%,transparent_60%)]" />

      {/* Content */}
      <div
        className="relative flex flex-1 flex-col items-center overflow-y-auto px-4 py-8 sm:px-8 sm:py-12"
        style={isMobileLayout ? { paddingTop: "var(--safe-area-top, 0px)", paddingBottom: "var(--safe-area-bottom, 0px)" } : undefined}
      >
        {/* Header */}
        <div className="mb-10 flex flex-col items-center text-center animate-[fade-in_0.3s_ease-out]">
          <div className="mb-4 size-11 overflow-hidden rounded-xl">
            <img src="/cord-logo.svg" alt="Cord" className="size-full" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight text-zinc-100">
            Welcome to Cord
          </h1>
          <p className="mt-2 max-w-md text-sm text-zinc-500">
            Let's get your workspace ready. This takes about two minutes.
          </p>
        </div>

        {/* Progress bar — hidden when the post-repo decision card is active
            because the user has effectively completed all steps at that point */}
        {!postRepoDecisionActive && (
          <div className="mb-8 w-full max-w-lg animate-[fade-in_0.3s_ease-out]">
            <ProgressBar
              currentStep={currentStep}
              step1Complete={step1Acknowledged}
              hasGitHub={hasGitHub}
            />
          </div>
        )}

        {/* Step content */}
        <div className="w-full max-w-lg animate-[slide-in-up_0.25s_ease-out]" key={currentStep}>
          {currentStep === 1 && (
            <StepModelProvider onComplete={handleStep1Complete} />
          )}
          {currentStep === 2 && (
            <StepGitHub
              userId={userId}
              allowExternalScope={allowPublicImportExternalScope}
              allowOrgScope={allowPublicImportOrgScope}
              onSkip={handleSkipGitHub}
              onPostRepoDecisionActive={() => setPostRepoDecisionActive(true)}
            />
          )}
          {currentStep === 3 && (
            <StepRepository
              userId={userId}
              allowExternalScope={allowPublicImportExternalScope}
              allowOrgScope={allowPublicImportOrgScope}
              onPostRepoDecisionActive={() => setPostRepoDecisionActive(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

function ProgressBar({
  currentStep,
  step1Complete,
  hasGitHub,
}: {
  currentStep: OnboardingStep;
  step1Complete: boolean;
  hasGitHub: boolean;
}) {
  const isMobile = useMobileLayout();
  const completionMap: Record<OnboardingStep, boolean> = {
    1: step1Complete,
    2: hasGitHub,
    3: false, // step 3 navigates away on completion
  };

  return (
    <ol className="flex items-center gap-0" role="list" aria-label="Onboarding progress">
      {STEPS.map((s, idx) => {
        const isCompleted = completionMap[s.step];
        const isActive = s.step === currentStep;
        const isFuture = s.step > currentStep;

        return (
          <li
            key={s.step}
            className={cn("flex items-center", idx < STEPS.length - 1 && "flex-1")}
            aria-current={isActive ? "step" : undefined}
          >
            <div className="flex items-center gap-2">
              {/* Circle — size-9 on mobile for 36px touch target */}
              <div
                className={cn(
                  "flex shrink-0 items-center justify-center rounded-full text-xs font-medium transition-all duration-300",
                  isMobile ? "size-9" : "size-7",
                  isCompleted && "bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/30",
                  isActive && !isCompleted && "bg-zinc-100 text-zinc-900",
                  isFuture && "bg-zinc-800/60 text-zinc-600",
                )}
                aria-label={`Step ${s.step}: ${s.label}${isCompleted ? " — completed" : isActive ? " — current" : ""}`}
              >
                {isCompleted ? <Check className="size-3.5" aria-hidden="true" /> : s.step}
              </div>
              {/* Label */}
              <div className={cn("hidden sm:block", (isActive || isCompleted) && "block")}>
                <div
                  className={cn(
                    "text-xs font-medium whitespace-nowrap",
                    isCompleted && "text-emerald-400/80",
                    isActive && !isCompleted && "text-zinc-200",
                    isFuture && "text-zinc-600",
                  )}
                >
                  {s.label}
                </div>
              </div>
            </div>
            {/* Connector line */}
            {idx < STEPS.length - 1 && (
              <div
                className={cn(
                  "mx-3 h-px flex-1 transition-colors duration-300",
                  isCompleted ? "bg-emerald-500/30" : "bg-zinc-800/60",
                )}
                aria-hidden="true"
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Models — Two-Zone Layout
// ---------------------------------------------------------------------------

function StepModelProvider({ onComplete }: { onComplete: () => void }) {
  const isMobile = useMobileLayout();
  const queryClient = useQueryClient();

  // Track which BYO connection row is expanded
  const [expanded, setExpanded] = useState<
    null | "api-key" | "claude-oauth" | "chatgpt-oauth"
  >(null);

  // API Key state
  const [provider, setProvider] = useState("anthropic");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [baseUrl, setBaseUrl] = useState("");
  const apiKeyInputRef = useRef<HTMLInputElement>(null);

  // Claude OAuth state
  const [claudeOauthToken, setClaudeOauthToken] = useState("");
  const [claudeOauthError, setClaudeOauthError] = useState<string | null>(null);
  const claudeTokenInputRef = useRef<HTMLInputElement>(null);

  // ChatGPT OAuth state
  const [chatGptPending, setChatGptPending] = useState<ChatGptOauthStartResponse | null>(null);
  const [chatGptError, setChatGptError] = useState<string | null>(null);
  const chatGptPollStartRef = useRef<number>(0);

  // CLI command copy state
  const [copied, setCopied] = useState(false);

  // Track when Claude polling started to enforce the max duration
  const claudePollStartRef = useRef<number>(0);
  const [claudePollTimedOut, setClaudePollTimedOut] = useState(false);

  // Reset polling start time when the Claude row is expanded
  useEffect(() => {
    if (expanded === "claude-oauth") {
      claudePollStartRef.current = Date.now();
      setClaudePollTimedOut(false);
    }
  }, [expanded]);

  // Focus API key input on expand
  useEffect(() => {
    if (expanded === "api-key") {
      requestAnimationFrame(() => apiKeyInputRef.current?.focus());
    }
  }, [expanded]);

  // ---- Claude OAuth polling ----
  const { data: claudeOauthStatus, isFetching: claudeOauthStatusFetching, refetch: refetchClaudeOauthStatus } = useQuery<ClaudeOauthStatus>({
    queryKey: ["claude-oauth-status"],
    queryFn: async () => {
      const res = await api.api.settings["claude-oauth"].status.$get();
      if (!res.ok) return { connected: false };
      return res.json();
    },
    // Poll every 5s, but only when the Claude row is expanded and not timed out.
    // The timeout check lives here (not in queryFn) so the query function stays
    // a pure data-fetch without React state side effects.
    enabled: expanded === "claude-oauth" && !claudePollTimedOut,
    refetchInterval: (query) => {
      if (query.state.data?.connected) return false;
      // Stop polling if duration exceeded
      if (claudePollStartRef.current > 0 && Date.now() - claudePollStartRef.current > CLAUDE_POLL_MAX_DURATION_MS) {
        setClaudePollTimedOut(true);
        return false;
      }
      return 5000;
    },
  });

  // ---- Shared success handler ----
  const invalidateAndComplete = useCallback(() => {
    invalidateModelProviderQueries({ queryClient });
    // Brief delay so user sees the success state before advancing
    setTimeout(() => {
      onComplete();
    }, SUCCESS_ADVANCE_DELAY_MS);
  }, [queryClient, onComplete]);

  // Auto-complete when Claude polling detects connection
  useEffect(() => {
    if (expanded === "claude-oauth" && claudeOauthStatus?.connected) {
      toast.success("Claude subscription connected");
      invalidateAndComplete();
    }
  }, [claudeOauthStatus?.connected, invalidateAndComplete, expanded]);

  // ---- Mutations ----

  const addKey = useMutation({
    mutationFn: async () => {
      const res = await api.api.settings["api-keys"].$post({
        json: {
          provider,
          apiKey: apiKey.trim(),
          baseUrl: baseUrl.trim() || undefined,
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error((body as { error?: string } | null)?.error ?? "Failed to save key");
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success("API key added");
      invalidateAndComplete();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to add API key");
    },
  });

  const connectClaudeOauth = useMutation({
    mutationFn: async () => {
      const res = await api.api.settings["claude-oauth"].connect.$post({
        json: { token: claudeOauthToken.trim() },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error((body as { error?: string } | null)?.error ?? "Failed to connect Claude subscription");
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success("Claude subscription connected");
      invalidateAndComplete();
    },
    onError: (err) => {
      setClaudeOauthError(err instanceof Error ? err.message : "Failed to connect");
    },
  });

  // ---- ChatGPT OAuth ----

  const startChatGptOauth = useMutation({
    mutationFn: async () => {
      const res = await api.api.settings["chatgpt-oauth"].start.$post();
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error((body as { error?: string } | null)?.error ?? "Failed to start authorization");
      }
      return (await res.json()) as ChatGptOauthStartResponse;
    },
    onSuccess: (payload) => {
      chatGptPollStartRef.current = Date.now();
      setChatGptPending(payload);
      setChatGptError(null);
    },
    onError: (err) => {
      setChatGptError(err instanceof Error ? err.message : "Failed to start authorization");
    },
  });

  const cancelChatGptOauth = useCallback(() => {
    setChatGptPending(null);
    setChatGptError(null);
  }, []);

  // Poll for ChatGPT completion
  useEffect(() => {
    if (!chatGptPending) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      if (Date.now() - chatGptPollStartRef.current > CHATGPT_POLL_MAX_DURATION_MS) {
        if (!cancelled) {
          setChatGptError("Authorization timed out. Please try again.");
          setChatGptPending(null);
        }
        return;
      }

      try {
        const res = await api.api.settings["chatgpt-oauth"].complete.$post({
          json: {
            deviceAuthId: chatGptPending.deviceAuthId,
            userCode: chatGptPending.userCode,
          },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error((body as { error?: string } | null)?.error ?? "Authorization failed");
        }

        const result = (await res.json()) as ChatGptOauthCompleteResponse;
        if (cancelled) return;

        if (result.status === "connected") {
          setChatGptPending(null);
          toast.success("ChatGPT subscription connected");
          invalidateAndComplete();
          return;
        }
      } catch (err) {
        if (cancelled) return;
        setChatGptError(err instanceof Error ? err.message : "Authorization failed");
        setChatGptPending(null);
        return;
      }

      if (cancelled) return;
      timeoutId = setTimeout(poll, Math.max(chatGptPending.intervalSeconds, CHATGPT_POLL_MIN_INTERVAL_S) * 1000);
    };

    void poll();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [chatGptPending, invalidateAndComplete]);

  // ---- Copy CLI commands ----
  const cliCommands = "npx cord-cli auth login && npx cord-cli providers connect claude";
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(cliCommands);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [cliCommands]);

  // ---- Render ----

  return (
    <div className="space-y-3">

      {/* ===== 1. INCLUDED FREE MODELS ===== */}
      <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/40 px-4 py-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
            Included models
          </span>
          <span className="text-xs text-zinc-600">no setup needed</span>
        </div>
        <div className="mb-4 flex flex-wrap gap-1.5">
          {FREE_MODELS.map((model) => (
            <span
              key={model}
              className="rounded-md border border-zinc-700/40 bg-zinc-800/40 px-2.5 py-1 text-xs font-medium text-zinc-300"
            >
              {model}
            </span>
          ))}
        </div>
        <button
          type="button"
          onClick={onComplete}
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-lg bg-zinc-100 px-4 font-semibold text-zinc-900 transition-all hover:bg-white",
            isMobile ? "py-3.5 text-[15px]" : "py-2.5 text-sm",
          )}
        >
          Continue
          <ArrowRight className="size-4" />
        </button>
      </div>

      {/* ===== 2. CONNECT YOUR OWN ===== */}
      <div className="space-y-1.5">
        <p className="px-1 pb-0.5 text-xs font-medium uppercase tracking-wider text-zinc-600">
          Connect your own
        </p>

        {/* API Key */}
        <CommercialOptionRow
          icon={<Key className="size-4" />}
          title="API Key"
          description="Anthropic, OpenAI, Google, or other providers"
          expanded={expanded === "api-key"}
          onToggle={() => setExpanded(expanded === "api-key" ? null : "api-key")}
        >
              <div className="space-y-3 pt-3">
                <div>
                  <label className="mb-1 block text-xs text-zinc-400">Provider</label>
                  <select
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    className={cn(
                      "w-full rounded-lg border border-zinc-700/50 bg-zinc-800/50 px-3 text-zinc-100 outline-none focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600",
                      isMobile ? "py-3 text-[15px]" : "py-2 text-sm",
                    )}
                  >
                    {API_KEY_PROVIDERS.map((p) => (
                      <option key={p.id} value={p.id} className="bg-zinc-900 text-zinc-100">
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-zinc-400">API Key</label>
                  <div className="relative">
                    <input
                      ref={apiKeyInputRef}
                      type={showKey ? "text" : "password"}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder={provider === "anthropic" ? "sk-ant-..." : provider === "openai" ? "sk-..." : "Paste your API key"}
                      className={cn(
                        "w-full rounded-lg border border-zinc-700/50 bg-zinc-800/50 px-3 pr-9 text-zinc-100 placeholder-zinc-600 outline-none focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600",
                        isMobile ? "py-3 text-[15px]" : "py-2 text-sm",
                      )}
                    />
                    <button
                      type="button"
                      onClick={() => setShowKey(!showKey)}
                      aria-label={showKey ? "Hide API key" : "Show API key"}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {showKey ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                    </button>
                  </div>
                </div>
                {/* Advanced options */}
                <details className="group">
                  <summary className="cursor-pointer text-xs text-zinc-600 hover:text-zinc-400">
                    Advanced options
                  </summary>
                  <div className="mt-2">
                    <label className="mb-1 block text-xs text-zinc-400">Custom Base URL</label>
                    <input
                      type="url"
                      value={baseUrl}
                      onChange={(e) => setBaseUrl(e.target.value)}
                      placeholder="https://api.example.com/v1"
                      className={cn(
                        "w-full rounded-lg border border-zinc-700/50 bg-zinc-800/50 px-3 text-zinc-100 placeholder-zinc-600 outline-none focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600",
                        isMobile ? "py-3 text-[15px]" : "py-2 text-sm",
                      )}
                    />
                  </div>
                </details>
                <button
                  type="button"
                  disabled={!apiKey.trim() || addKey.isPending}
                  onClick={() => addKey.mutate()}
                  className={cn(
                    "w-full rounded-lg bg-zinc-100 px-3 font-medium text-zinc-900 transition-colors hover:bg-white disabled:opacity-50",
                    isMobile ? "py-3 text-[15px]" : "py-2 text-sm",
                  )}
                >
                  {addKey.isPending ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="size-3.5 animate-spin" />
                      Verifying…
                    </span>
                  ) : (
                    "Add API key"
                  )}
                </button>
              </div>
            </CommercialOptionRow>

            {/* Claude Subscription */}
            <CommercialOptionRow
              icon={<Terminal className="size-4" />}
              title="Claude Pro / Max"
              description="Connect via Cord CLI"
              expanded={expanded === "claude-oauth"}
              onToggle={() => setExpanded(expanded === "claude-oauth" ? null : "claude-oauth")}
            >
              <div className="space-y-3 pt-3">
                {/* CLI commands */}
                <div className="rounded-lg border border-zinc-700/50 bg-zinc-950/70 p-3">
                  <p className="mb-2 text-xs font-medium text-zinc-400">Run in your terminal (requires Node.js 18+):</p>
                  <div className="relative">
                    <pre className="overflow-x-auto rounded-md bg-zinc-900 px-3 py-2 pr-10 text-xs text-zinc-200">
                      <code>{`$ npx cord-cli auth login\n$ npx cord-cli providers connect claude`}</code>
                    </pre>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="absolute right-2 top-2 rounded p-1 text-zinc-500 hover:text-zinc-300"
                      aria-label="Copy commands"
                    >
                      {copied ? <Check className="size-3.5 text-emerald-400" /> : <Copy className="size-3.5" />}
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-zinc-600">
                    Opens your browser to authorize with Anthropic. Requires a Claude Pro or Max plan.
                  </p>
                </div>

                {/* Live status */}
                <div className="flex items-center gap-2 px-1">
                  {claudeOauthStatus?.connected ? (
                    <>
                      <Check className="size-3.5 text-emerald-400" />
                      <span className="text-xs text-emerald-400">Connected</span>
                    </>
                  ) : claudePollTimedOut ? (
                    <>
                      <div className="size-2 rounded-full bg-amber-500" />
                      <span className="text-xs text-amber-400">Polling stopped — click below to check manually</span>
                    </>
                  ) : (
                    <>
                      <div className="size-2 animate-pulse rounded-full bg-zinc-500" />
                      <span className="text-xs text-zinc-500">Listening for connection…</span>
                    </>
                  )}
                </div>

                {/* Manual check button */}
                <button
                  type="button"
                  onClick={() => {
                    // If timed out, restart polling for another cycle
                    if (claudePollTimedOut) {
                      claudePollStartRef.current = Date.now();
                      setClaudePollTimedOut(false);
                    }
                    void refetchClaudeOauthStatus();
                  }}
                  disabled={claudeOauthStatusFetching}
                  className="w-full rounded-lg border border-zinc-700/50 px-3 py-2 text-xs text-zinc-400 transition-colors hover:border-zinc-600 hover:text-zinc-200 disabled:opacity-50"
                >
                  {claudeOauthStatusFetching ? "Checking…" : claudePollTimedOut ? "Check again" : "I've connected Claude"}
                </button>

                {claudeOauthError && (
                  <p className="text-xs text-red-400">{claudeOauthError}</p>
                )}

                {/* Fallback: manual token */}
                <details className="rounded-lg border border-zinc-700/40 bg-zinc-900/30 p-3">
                  <summary className="cursor-pointer text-xs text-zinc-500 hover:text-zinc-400">
                    Fallback: paste a setup-token manually
                  </summary>
                  <div className="mt-2 space-y-2">
                    <p className="text-xs text-zinc-600">
                      If you have Claude Code installed locally: <code className="rounded bg-zinc-800 px-1 py-0.5 text-xs text-zinc-400">cord providers connect claude --setup-token</code>
                    </p>
                    <p className="text-xs text-zinc-600">
                      Or paste a token directly:
                    </p>
                    <input
                      ref={claudeTokenInputRef}
                      type="password"
                      value={claudeOauthToken}
                      onChange={(e) => {
                        setClaudeOauthToken(e.target.value);
                        setClaudeOauthError(null);
                      }}
                      placeholder="sk-ant-oat01-..."
                      className="w-full rounded-lg border border-zinc-700/50 bg-zinc-800/50 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600"
                    />
                    <button
                      type="button"
                      disabled={!claudeOauthToken.trim() || connectClaudeOauth.isPending}
                      onClick={() => connectClaudeOauth.mutate()}
                      className="w-full rounded-lg bg-zinc-100 px-3 py-2 text-xs font-medium text-zinc-900 transition-colors hover:bg-white disabled:opacity-50"
                    >
                      {connectClaudeOauth.isPending ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="size-3 animate-spin" />
                          Connecting…
                        </span>
                      ) : (
                        "Connect token"
                      )}
                    </button>
                  </div>
                </details>
              </div>
            </CommercialOptionRow>

            {/* ChatGPT Subscription */}
            <CommercialOptionRow
              icon={<CreditCard className="size-4" />}
              title="ChatGPT Plus / Pro"
              description="Device authorization"
              expanded={expanded === "chatgpt-oauth"}
              onToggle={() => setExpanded(expanded === "chatgpt-oauth" ? null : "chatgpt-oauth")}
            >
              <div className="space-y-3 pt-3">
                {/* Prerequisite: enable device code auth in ChatGPT settings */}
                {!chatGptPending && (
                  <div className="rounded-lg border border-zinc-700/50 bg-zinc-950/70 p-3">
                    <p className="mb-2 text-xs font-medium text-zinc-400">Before connecting, enable device authorization:</p>
                    <ol className="space-y-1.5 text-xs text-zinc-500">
                      <li className="flex gap-2">
                        <span className="shrink-0 text-zinc-600">1.</span>
                        <span>
                          Open{" "}
                          <a
                            href="https://chatgpt.com/#settings/Security"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-zinc-300 underline underline-offset-2 hover:text-zinc-100"
                          >
                            ChatGPT Settings → Security
                          </a>
                        </span>
                      </li>
                      <li className="flex gap-2">
                        <span className="shrink-0 text-zinc-600">2.</span>
                        <span>Toggle ON: <span className="text-zinc-300">"Enable device code authorization for Codex"</span></span>
                      </li>
                    </ol>
                  </div>
                )}

                {/* Start button or loading state */}
                {!chatGptPending && !chatGptError && (
                  <button
                    type="button"
                    disabled={startChatGptOauth.isPending}
                    onClick={() => startChatGptOauth.mutate()}
                    className="w-full rounded-lg bg-zinc-100 px-3 py-2 text-sm font-medium text-zinc-900 transition-colors hover:bg-white disabled:opacity-50"
                  >
                    {startChatGptOauth.isPending ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="size-3.5 animate-spin" />
                        Starting…
                      </span>
                    ) : (
                      "Connect ChatGPT"
                    )}
                  </button>
                )}

                {chatGptError && !chatGptPending && (
                  <div className="space-y-2">
                    <p className="text-xs text-red-400">{chatGptError}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setChatGptError(null);
                        startChatGptOauth.mutate();
                      }}
                      className="w-full rounded-lg border border-zinc-700/50 px-3 py-2 text-xs text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                    >
                      Try again
                    </button>
                  </div>
                )}

                {chatGptPending && (
                  <div className="space-y-3">
                    {/* User code */}
                    <div className="rounded-lg border border-zinc-700/50 bg-zinc-800/30 p-4 text-center">
                      <p className="mb-1.5 text-xs text-zinc-500">
                        Enter this code at ChatGPT:
                      </p>
                      <p className="font-mono text-2xl font-bold tracking-widest text-zinc-100">
                        {chatGptPending.userCode}
                      </p>
                    </div>

                    {/* Open ChatGPT link */}
                    <a
                      href={chatGptPending.verificationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center justify-center gap-2 rounded-lg bg-zinc-100 px-3 py-2 text-sm font-medium text-zinc-900 transition-colors hover:bg-white"
                    >
                      Open ChatGPT
                      <ExternalLink className="size-3.5" />
                    </a>

                    {/* Polling indicator */}
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="size-3 animate-spin text-zinc-500" />
                      <span className="text-xs text-zinc-500">Waiting for authorization…</span>
                    </div>

                    {/* Cancel */}
                    <button
                      type="button"
                      onClick={cancelChatGptOauth}
                      className="w-full text-center text-xs text-zinc-600 hover:text-zinc-400"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </CommercialOptionRow>
        </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2: GitHub App Installation
// ---------------------------------------------------------------------------

function StepGitHub({
  userId,
  allowExternalScope,
  allowOrgScope,
  onSkip,
  onPostRepoDecisionActive,
}: {
  userId: string;
  allowExternalScope: boolean;
  allowOrgScope: boolean;
  onSkip: () => void;
  onPostRepoDecisionActive: () => void;
}) {
  const isMobile = useMobileLayout();
  const markProgress = useMarkOnboardingProgress();
  const [connecting, setConnecting] = useState(false);
  const apiBase = import.meta.env.VITE_API_URL || "";
  const [decidedRepo, setDecidedRepo] = useState<{
    id: string;
    name: string;
    defaultBranch: string | null;
  } | null>(null);
  const [selectedDemoSlug, setSelectedDemoSlug] = useState<string | null>(null);
  const [showAlternatives, setShowAlternatives] = useState(false);

  // Ref for the navigation timeout so we can clean it up on unmount or retry.
  const connectGhTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (connectGhTimeoutRef.current !== null) {
        clearTimeout(connectGhTimeoutRef.current);
        connectGhTimeoutRef.current = null;
      }
    };
  }, []);

  // During onboarding we always use "org" scope so the installation is
  // shared with the whole team. Users who need a private/personal install
  // can add one later from Settings.
  const ONBOARDING_SCOPE = "org" as const;

  const connectGitHubApp = async () => {
    setConnecting(true);
    // Clear any previous navigation timeout (e.g. user retries quickly)
    if (connectGhTimeoutRef.current !== null) {
      clearTimeout(connectGhTimeoutRef.current);
      connectGhTimeoutRef.current = null;
    }
    try {
      const url = apiBase
        ? `${apiBase}/api/github/app/install-url?scope=${ONBOARDING_SCOPE}&origin=onboarding`
        : `/api/github/app/install-url?scope=${ONBOARDING_SCOPE}&origin=onboarding`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        setConnecting(false);
        toast.error("Failed to get GitHub install URL");
        return;
      }
      const data = (await res.json()) as { installUrl: string };
      window.location.href = data.installUrl;
      // If the browser hasn't navigated away after the timeout, reset the
      // button state. This handles cases where navigation is blocked by a
      // popup blocker or browser extension.
      connectGhTimeoutRef.current = setTimeout(() => {
        connectGhTimeoutRef.current = null;
        setConnecting(false);
      }, CONNECT_GITHUB_NAVIGATION_TIMEOUT_MS);
    } catch {
      setConnecting(false);
      toast.error("Failed to connect to GitHub");
    }
  };

  const handleProjectCreated = useCallback(
    (repo: ImportedRepositoryDto | CreatedProjectDto) => {
      markOnboardingComplete(userId);
      markProgress.mutate({ completed: true });
      onPostRepoDecisionActive();
      setDecidedRepo({
        id: repo.id,
        name: repo.displayName ?? repo.repositoryFullName ?? repo.name,
        defaultBranch: repo.defaultBranch ?? null,
      });
    },
    [userId, onPostRepoDecisionActive, markProgress.mutate],
  );

  if (decidedRepo) {
    return (
      <PostRepoDecisionCard
        repoId={decidedRepo.id}
        repoName={decidedRepo.name}
        defaultBranch={decidedRepo.defaultBranch}
      />
    );
  }

  const selectedDemo = selectedDemoSlug
    ? DEMO_PROJECTS.find((d) => d.slug === selectedDemoSlug) ?? null
    : null;

  const hasDemos = allowExternalScope && DEMO_PROJECTS.length > 0;

  return (
    <div className="space-y-0">
      {/* ─── Connect GitHub (primary CTA) ─── */}
      <button
        type="button"
        disabled={connecting}
        onClick={() => connectGitHubApp()}
        className="group flex w-full items-center gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.03] px-5 py-4 text-left transition-all duration-200 hover:border-white/[0.12] hover:bg-white/[0.05] active:scale-[0.99] disabled:opacity-50"
      >
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] text-zinc-300 transition-colors group-hover:bg-white/[0.1] group-hover:text-white">
          {connecting ? (
            <Loader2 className="size-[18px] animate-spin" />
          ) : (
            <Github className="size-[18px]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <span className="block text-[14px] font-medium text-zinc-100">
            Connect GitHub
          </span>
          <span className="block text-[12px] text-zinc-500">
            Import your repositories and start coding
          </span>
        </div>
        <ArrowRight className="size-4 text-zinc-600 transition-colors group-hover:text-zinc-400" />
      </button>

      {/* ─── Disclosure toggle for secondary paths ─── */}
      <button
        onClick={() => setShowAlternatives((prev) => !prev)}
        className={cn(
          "mt-5 flex w-full items-center justify-center gap-1.5 text-zinc-500 transition-colors hover:text-zinc-300",
          isMobile ? "py-2 text-[14px]" : "py-1 text-[12px]",
        )}
      >
        <span>or start without GitHub</span>
        <ChevronDown
          className="size-3.5 transition-transform duration-200"
          style={{ transform: showAlternatives ? "rotate(180deg)" : undefined }}
        />
      </button>

      {/* ─── Expandable secondary options ─── */}
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: showAlternatives ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="pt-4">
            {/* Smart input — bare, no card wrapper */}
            <SmartProjectInput
              allowExternalScope={allowExternalScope}
              allowOrgScope={allowOrgScope}
              onCreated={handleProjectCreated}
              autoFocus={showAlternatives}
            />

            {/* Demo links — compact inline row */}
            {hasDemos && (
              <div className={cn(
                "mt-4 flex flex-wrap items-center justify-center gap-x-1",
                isMobile ? "text-[14px]" : "text-[12px]",
              )}>
                <span className="text-zinc-600">or try a demo:</span>
                {DEMO_PROJECTS.map((demo, i) => (
                  <span key={demo.slug} className="inline-flex items-center">
                    <button
                      onClick={() => setSelectedDemoSlug(demo.slug)}
                      className="text-zinc-500 underline decoration-zinc-700 underline-offset-2 transition-colors hover:text-zinc-300 hover:decoration-zinc-500"
                    >
                      {demo.title}
                    </button>
                    {i < DEMO_PROJECTS.length - 1 && (
                      <span className="mx-0.5 text-zinc-700">&middot;</span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Skip ─── */}
      {!isMobile && (
        <div className="pt-4 text-center">
          <button
            type="button"
            onClick={onSkip}
            className="text-[12px] text-zinc-600 transition-colors hover:text-zinc-400"
          >
            Skip for now
          </button>
        </div>
      )}

      {/* Demo launch modal */}
      <DemoLaunchModal
        demo={selectedDemo}
        open={selectedDemo !== null}
        onClose={() => setSelectedDemoSlug(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Repository Connection
// ---------------------------------------------------------------------------

function StepRepository({
  userId,
  allowExternalScope,
  allowOrgScope,
  onPostRepoDecisionActive,
}: {
  userId: string;
  allowExternalScope: boolean;
  allowOrgScope: boolean;
  onPostRepoDecisionActive: () => void;
}) {
  const isMobile = useMobileLayout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const markProgress = useMarkOnboardingProgress();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Post-repo decision screen state. When set (i.e. after the user connects
  // a repo), we swap the repo-picker UI for the decision card that lets them
  // either configure the dev environment or jump into their first session.
  const [connectedRepo, setConnectedRepo] = useState<{
    id: string;
    name: string;
    defaultBranch: string | null;
  } | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  // Fetch available GitHub repos
  const { data: githubReposData, isLoading: ghLoading, isError: ghError, refetch: refetchGhRepos } = useQuery<GitHubReposResponse>({
    queryKey: ["github-repos"],
    queryFn: async () => {
      const res = await api.api.github.repos.$get();
      if (!res.ok) throw new Error("Failed to fetch repos");
      return normalizeGitHubReposRouteResponse<GitHubRepo>({
        value: await res.json(),
      });
    },
  });

  // Fetch already-connected repos
  const { data: connectedRepos } = useQuery<ConnectedRepo[]>({
    queryKey: ["repos"],
    queryFn: async () => {
      const res = await api.api.repos.$get();
      if (!res.ok) return [];
      return normalizeArrayResponse<ConnectedRepo>({ value: await res.json() });
    },
  });

  const githubRepos = githubReposData?.repos ?? [];
  const installationErrors = githubReposData?.installationErrors ?? [];
  const connectedIds = new Set(
    (connectedRepos ?? [])
      .filter((repo) => (repo.provider ?? "github") === "github" && typeof repo.githubId === "number")
      .map((repo) => repo.githubId),
  );
  const available = githubRepos.filter((r) => !connectedIds.has(r.id));

  // Detect the "0 repos granted" scenario: GitHub App is installed (user got
  // to Step 3) but no repos are visible. This happens when the user selects
  // "Only select repositories" on GitHub and picks none.
  // We exclude the case where installationErrors are present — that indicates
  // a transient fetch failure, not a genuine zero-grant state.
  const hasZeroReposGranted = !ghLoading && !ghError
    && installationErrors.length === 0
    && githubRepos.length === 0
    && (connectedRepos ?? []).length === 0;

  const filtered = search.trim()
    ? available.filter((r) =>
        r.full_name.toLowerCase().includes(search.toLowerCase()),
      )
    : available;

  const visibleFiltered = filtered.slice(0, REPO_DROPDOWN_MAX_ITEMS);
  const isTruncated = filtered.length > REPO_DROPDOWN_MAX_ITEMS;

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  const connectRepo = useMutation({
    mutationFn: async (repo: GitHubRepo) => {
      const res = await api.api.repos.$post({
        json: {
          githubId: repo.id,
          githubFullName: repo.full_name,
          githubInstallationId: repo.github_installation_id,
          connectScope: repo.source_scope === "external" ? "external" : "org",
          name: repo.name,
          defaultBranch: repo.default_branch,
          cloneUrl: repo.clone_url,
          isPrivate: repo.private,
          language: repo.language,
        },
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => ({ error: "Failed to connect repo" }))) as {
          error?: string;
        };
        throw new Error(payload.error || "Failed to connect repo");
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data?.id) {
        upsertRepositoryInCache({ queryClient, repository: data });
      }
      queryClient.invalidateQueries({ queryKey: ["repos"] });
      markOnboardingComplete(userId);
      markProgress.mutate({ completed: true });
      // Instead of navigating directly to settings we hand control to the
      // in-place post-repo decision screen (rendered below) so the user can
      // choose between configuring the environment now or jumping straight
      // into their first session.
      if (data?.id) {
        onPostRepoDecisionActive();
        setConnectedRepo({
          id: data.id,
          name: data.name ?? "your repo",
          defaultBranch: data.defaultBranch ?? null,
        });
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to connect repo");
    },
  });

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        return;
      }

      if (!isOpen || filtered.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < visibleFiltered.length - 1 ? prev + 1 : 0,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : visibleFiltered.length - 1,
        );
      } else if (e.key === "Enter" && highlightedIndex >= 0) {
        e.preventDefault();
        const repo = visibleFiltered[highlightedIndex];
        if (repo && !connectRepo.isPending) {
          connectRepo.mutate(repo);
        }
      }
    },
    [isOpen, filtered.length, visibleFiltered, highlightedIndex, connectRepo],
  );

  // Scroll highlighted into view
  useEffect(() => {
    if (highlightedIndex < 0 || !dropdownRef.current) return;
    const items = dropdownRef.current.querySelectorAll("[data-repo-item]");
    items[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  // Skip handler — lets them explore the dashboard without losing progress
  const handleSkipRepo = useCallback(async () => {
    markRepoStepSkipped(userId);
    try {
      await markProgress.mutateAsync({ repoStepSkipped: true });
    } catch {
      toast.warning("Skipped in this browser, but we could not save that across devices.");
    }
    navigate({ to: "/" });
  }, [navigate, userId, markProgress.mutateAsync]);

  // ---- Post-repo decision screen ----
  // Once a repo is connected we swap the repo-picker UI for a compact
  // decision screen so the user can pick between configuring their dev
  // environment or jumping straight into their first session.
  if (connectedRepo) {
    return (
      <PostRepoDecisionCard
        repoId={connectedRepo.id}
        repoName={connectedRepo.name}
        defaultBranch={connectedRepo.defaultBranch}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* ─── Pick a repo (hero) ─── */}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
        <h2 className="mb-1 text-[14px] font-medium text-zinc-200">
          Pick a repository
        </h2>
        <p className="mb-4 text-[12px] text-zinc-500">
          Choose a GitHub repo to start coding, or search below.
        </p>

        {/* Loading state */}
        {ghLoading && !ghError && (
          <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-zinc-950/40 px-4 py-3.5">
            <Loader2 className="size-4 shrink-0 animate-spin text-zinc-400" />
            <span className="text-sm text-zinc-400">Loading repositories…</span>
          </div>
        )}

        {/* Error state with retry */}
        {ghError && (
          <div className="rounded-xl border border-red-500/20 bg-red-500/[0.04] px-4 py-3.5">
            <p className="text-sm text-red-300">Failed to load repositories</p>
            <p className="mt-1 text-xs text-zinc-500">
              There was a problem fetching your GitHub repositories. Check your connection and try again.
            </p>
            <button
              type="button"
              onClick={() => void refetchGhRepos()}
              className="mt-2 rounded-lg border border-zinc-700/50 px-3 py-1.5 text-xs text-zinc-400 transition-colors hover:border-zinc-600 hover:text-zinc-200"
            >
              Retry
            </button>
          </div>
        )}

        {/* Partial installation errors (API returned 200 but some installs failed) */}
        {!ghLoading && !ghError && installationErrors.length > 0 && githubRepos.length === 0 && (
          <div className="mb-3 rounded-xl border border-red-500/20 bg-red-500/[0.04] px-4 py-3.5">
            <p className="text-sm text-red-300">Some installations could not be loaded</p>
            <p className="mt-1 text-xs text-zinc-500">
              There was a problem fetching repositories from one or more GitHub App installations.
              This is usually temporary — try again in a moment.
            </p>
            <button
              type="button"
              onClick={() => void refetchGhRepos()}
              className="mt-2 rounded-lg border border-zinc-700/50 px-3 py-1.5 text-xs text-zinc-400 transition-colors hover:border-zinc-600 hover:text-zinc-200"
            >
              Retry
            </button>
          </div>
        )}

        {/* Zero repos granted warning */}
        {hasZeroReposGranted && (
          <div className="mb-3 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] px-4 py-3">
            <p className="text-sm font-medium text-amber-300">
              No repositories are accessible
            </p>
            <p className="mt-1 text-xs text-zinc-500">
              Your GitHub App was installed, but no repositories were granted access.
              Go to your{" "}
              <a
                href="https://github.com/settings/installations"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-400/80 underline underline-offset-2 hover:text-amber-300"
              >
                GitHub App settings
              </a>{" "}
              and add repositories to the installation, then refresh this page.
            </p>
          </div>
        )}

        {/* Search input */}
        {!ghLoading && !ghError && (
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-zinc-500" />
            <input
              ref={inputRef}
              type="text"
              placeholder={`Search ${available.length} available repos…`}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              onKeyDown={handleKeyDown}
              className="w-full rounded-xl border border-white/[0.06] bg-zinc-950/60 py-3 pl-10 pr-4 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-all duration-150 focus:border-zinc-500/50 focus:bg-zinc-950/80"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  inputRef.current?.focus();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Dropdown list */}
        {isOpen && !ghLoading && !ghError && (
          <div
            ref={dropdownRef}
            className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-white/[0.06] bg-zinc-950/80 shadow-xl shadow-black/40"
          >
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-zinc-600">
                {search
                  ? "No matching repos found"
                  : hasZeroReposGranted
                    ? "No repositories have been granted access"
                    : "All your repos are already connected"}
              </div>
            ) : (
              <>
                {visibleFiltered.map((repo, i) => (
                  <button
                    key={repo.id}
                    data-repo-item
                    onClick={() => connectRepo.mutate(repo)}
                    onMouseEnter={() => setHighlightedIndex(i)}
                    disabled={connectRepo.isPending}
                    className={cn(
                      "group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors disabled:opacity-50",
                      i === highlightedIndex
                        ? "bg-white/[0.06]"
                        : "hover:bg-white/[0.04]",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-zinc-200 group-hover:text-zinc-100">
                        {repo.full_name}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2">
                        {repo.language && (
                          <span className="flex items-center gap-1.5 text-xs text-zinc-500">
                            <span
                              className="inline-block size-2 rounded-full"
                              style={{
                                backgroundColor: langColors[repo.language] || "#71717a",
                              }}
                            />
                            {repo.language}
                          </span>
                        )}
                        {repo.private && (
                          <span className="text-xs text-zinc-600">Private</span>
                        )}
                        {repo.default_branch.length === 0 && <EmptyRepositoryBadge />}
                      </div>
                    </div>
                    <span
                      className={cn(
                        "shrink-0 rounded-lg border border-white/[0.06] bg-white/[0.04] px-2.5 py-1 text-xs text-zinc-400 transition-opacity",
                        i === highlightedIndex
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100",
                      )}
                    >
                      Connect
                    </span>
                  </button>
                ))}
                {isTruncated && (
                  <div className="px-4 py-2.5 text-center text-xs text-zinc-600">
                    Showing {REPO_DROPDOWN_MAX_ITEMS} of {filtered.length} repos — refine your search to see more
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ─── Or start fresh (smart input) ─── */}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
        <h2 className="mb-1 text-[14px] font-medium text-zinc-200">
          Or start fresh
        </h2>
        <p className="mb-4 text-[12px] text-zinc-500">
          Create a new project or import a public repo URL.
        </p>
        <SmartProjectInput
          allowExternalScope={allowExternalScope}
          allowOrgScope={allowOrgScope}
          onCreated={(repo) => {
            markOnboardingComplete(userId);
            markProgress.mutate({ completed: true });
            onPostRepoDecisionActive();
            setConnectedRepo({
              id: repo.id,
              name: repo.displayName ?? repo.repositoryFullName ?? repo.name,
              defaultBranch: repo.defaultBranch ?? null,
            });
          }}
        />
      </div>

      {/* ─── Skip ─── */}
      <div className="pt-1 text-center">
        <button
          type="button"
          onClick={handleSkipRepo}
          className={cn(
            "text-zinc-600 transition-colors hover:text-zinc-400",
            isMobile ? "py-2 text-[14px]" : "text-[12px]",
          )}
        >
          I'll pick a repo later
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Shared presentational components
// ---------------------------------------------------------------------------

function CommercialOptionRow({
  icon,
  title,
  description,
  expanded,
  onToggle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const isMobile = useMobileLayout();
  return (
    <div
      className={cn(
        "rounded-xl border transition-colors",
        expanded
          ? "border-zinc-700/60 bg-zinc-900/80"
          : "border-zinc-800/40 bg-zinc-900/40 hover:border-zinc-700/50",
      )}
    >
      {/* min-h-[48px] on mobile ensures at least 48px touch target */}
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "flex w-full items-center gap-3 px-4 text-left",
          isMobile ? "min-h-[48px] py-3.5" : "py-3",
        )}
      >
        {icon && (
          <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-zinc-800/60 text-zinc-500">
            {icon}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <span className="text-sm font-medium text-zinc-200">{title}</span>
          <span className="ml-2 text-xs text-zinc-500">{description}</span>
        </div>
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-zinc-500 transition-transform",
            expanded && "rotate-180",
          )}
        />
      </button>
      {expanded && (
        <div className="border-t border-zinc-800/40 px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  );
}
